// Analytics API service for v0.6.0

import type { AnalyticsResponse, AnalyticsQuery } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Get member analytics data
 */
export async function getMemberAnalytics(
  query?: AnalyticsQuery
): Promise<AnalyticsResponse> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const params = new URLSearchParams();
  if (query?.startDate) params.append('startDate', query.startDate);
  if (query?.endDate) params.append('endDate', query.endDate);

  const url = `${API_BASE}/api/member/analytics${params.toString() ? `?${params}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: '未知錯誤' } }));
    throw new Error(error.error?.message || '取得分析資料失敗');
  }

  return response.json();
}

/**
 * Export analytics as CSV
 */
export async function exportAnalytics(query?: AnalyticsQuery): Promise<Blob> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const params = new URLSearchParams();
  if (query?.startDate) params.append('startDate', query.startDate);
  if (query?.endDate) params.append('endDate', query.endDate);

  const url = `${API_BASE}/api/member/analytics/export${params.toString() ? `?${params}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('匯出失敗');
  }

  return response.blob();
}

/**
 * Download analytics CSV file
 */
export async function downloadAnalyticsCSV(
  query?: AnalyticsQuery
): Promise<void> {
  const blob = await exportAnalytics(query);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `analytics_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ============== Admin Analytics API (Phase 3.3) ==============

const ADMIN_API_URL = import.meta.env.VITE_API_URL || 'https://bonbalayelo-api-preview.bonbalayelo.workers.dev';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface ViewTrendData {
  date: string;
  views: number;
}

export interface TopProductData {
  productId: string;
  title: string;
  views: number;
}

export interface CategoryDistributionData {
  category: string;
  count: number;
  percentage: number;
}

export interface SearchKeywordData {
  keyword: string;
  count: number;
}

export interface StockAlertData {
  productId: string;
  title: string;
  currentStock: number;
  alertType: string;
}

async function getAdminAuthToken(): Promise<string | null> {
  return localStorage.getItem('auth_token');
}

export async function getViewTrend(params: {
  period?: 'day' | 'week' | 'month';
  startDate?: string;
  endDate?: string;
} = {}): Promise<ApiResponse<ViewTrendData[]>> {
  const token = await getAdminAuthToken();
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.set('period', params.period);
  if (params.startDate) queryParams.set('startDate', params.startDate);
  if (params.endDate) queryParams.set('endDate', params.endDate);

  const response = await fetch(`${ADMIN_API_URL}/api/admin/analytics/views/trend?${queryParams}`, {
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch view trend');
  }

  return response.json();
}

export async function getTopProducts(params: {
  limit?: number;
  period?: 'day' | 'week' | 'month';
  startDate?: string;
  endDate?: string;
} = {}): Promise<ApiResponse<TopProductData[]>> {
  const token = await getAdminAuthToken();
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.period) queryParams.set('period', params.period);
  if (params.startDate) queryParams.set('startDate', params.startDate);
  if (params.endDate) queryParams.set('endDate', params.endDate);

  const response = await fetch(`${ADMIN_API_URL}/api/admin/analytics/products/top?${queryParams}`, {
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch top products');
  }

  return response.json();
}

export async function getCategoryDistribution(params: {
  startDate?: string;
  endDate?: string;
} = {}): Promise<ApiResponse<CategoryDistributionData[]>> {
  const token = await getAdminAuthToken();
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.set('startDate', params.startDate);
  if (params.endDate) queryParams.set('endDate', params.endDate);

  const response = await fetch(`${ADMIN_API_URL}/api/admin/analytics/categories/distribution?${queryParams}`, {
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch category distribution');
  }

  return response.json();
}

export async function getSearchKeywords(params: {
  limit?: number;
  startDate?: string;
  endDate?: string;
} = {}): Promise<ApiResponse<SearchKeywordData[]>> {
  const token = await getAdminAuthToken();
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.startDate) queryParams.set('startDate', params.startDate);
  if (params.endDate) queryParams.set('endDate', params.endDate);

  const response = await fetch(`${ADMIN_API_URL}/api/admin/analytics/search/keywords?${queryParams}`, {
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch search keywords');
  }

  return response.json();
}

export async function getStockAlerts(params: {
  threshold?: number;
} = {}): Promise<ApiResponse<StockAlertData[]>> {
  const token = await getAdminAuthToken();
  const queryParams = new URLSearchParams();
  if (params.threshold) queryParams.set('threshold', String(params.threshold));

  const response = await fetch(`${ADMIN_API_URL}/api/admin/analytics/stock/alerts?${queryParams}`, {
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch stock alerts');
  }

  return response.json();
}
